import React from 'react';
import AssignmentChip from './AssignmentChip';
import { DAY_NAMES } from '../constants';
import { toLocalISO, snapTo15Min, toDateKey } from '../../../../../utils/dateUtils';
import { getTimedLayout } from '../hooks/interactionHelpers';

/**
 * TimelineDayRow Component
 * 
 * Renders a single horizontal day row within the timeline grid.
 * Manages the layout of timed assignments and interaction states for a specific date.
 * 
 * @param {Object} props
 * @param {Date} props.day - The Date object for this row
 * @param {number} props.dayIdx - Index of the day in the current view
 * @param {number[]} props.dayHeights - Array of heights for all days
 * @param {Object} props.timedByKey - Grouped timed assignments
 * @param {Object} props.interaction - Current drag/resize state
 * @param {Function} props.getTimedLayout - Helper for calculating assignment positions
 * @param {Function} props.isSameDay - Utility to compare dates
 * @param {Date} props.today - Today's date for highlighting
 * @param {number} props.actualAlldayWidth - Width of the all-day column
 * @param {boolean} props.isReadOnly - Whether interactions are disabled
 * @param {number} props.TIMELINE_WIDTH - Total width of the hour grid
 * @param {number} props.hourStart - Grid start hour
 * @param {number} props.dynamicHourWidth - Width per hour slot
 * @param {Function} props.onMouseDownTimed - Handler for starting timed interactions
 * @param {boolean} props.isConflictDetectionEnabled - Global toggle
 * @param {Function} props.getConflicts - Conflict detection helper
 * @param {Function} props.onClickTimedGrid - Handler for clicking the grid background
 * @returns {JSX.Element}
 */
export function TimelineDayRow({
    day,
    dayIdx,
    dayHeights,
    timedByKey,
    interaction,
    getTimedLayout,
    isSameDay,
    today,
    actualAlldayWidth,
    isReadOnly,
    handleDropAllDay,
    handleDragOver,
    handleDragLeave,
    interactionEnded,
    onDayClick,
    handleContextMenu,
    TIMELINE_WIDTH,
    hourStart,
    hourEnd,
    dynamicHourWidth,
    GRID_BUFFER,
    nowHour,
    nowLeft,
    assignmentConflictsMap,
    isConflictDetectionEnabled,
    handleDrop,
    onAssignmentHover,
    onAssignmentHoverEnd,
    onConflictHover,
    onConflictHoverEnd,
    onAssignmentClick,
    handleInteractionStart,
    scrollX,
    setInteraction
}) {
    const key = toDateKey(day);
    const dayActualHeight = dayHeights[dayIdx];
    const isToday = isSameDay(day, today);
    const isWeekend = day.getDay() === 0 || day.getDay() === 6;

    // Ghost Logic for Previews during Drag
    let dayTimedEvents = timedByKey[key] || [];
    if ((interaction?.type === 'move' || interaction?.type === 'move-allday') && interaction.assignment && interaction.moved) {
        const dragId = interaction.assignment.id;
        const targetKey = toDateKey(interaction.currentDay || new Date(interaction.origS));
        const isCurrentlyTimedPreview = interaction.currentS && !interaction.isOverAllDay;

        dayTimedEvents = dayTimedEvents.filter(a => String(a.id) !== String(dragId));

        const hasInteractionTimes = interaction && interaction.currentS && interaction.currentE;
        const isSuppressedBySidebar = interaction && interaction.isOverSidebar;
        if (key === targetKey && isCurrentlyTimedPreview && hasInteractionTimes && !isSuppressedBySidebar) {
            const ghostId = 'ghost-' + dragId;
            if (!dayTimedEvents.find(a => String(a.id) === String(ghostId))) {
                const previewAssignment = {
                    ...interaction.assignment,
                    id: ghostId,
                    isGhost: true,
                    status: (interaction.assignment.status === 'undatiert' || interaction.assignment.status === 'geplant' || !interaction.assignment.status) ? 'aktiv' : interaction.assignment.status,
                    start_datetime: toLocalISO(interaction.currentS),
                    end_datetime: toLocalISO(interaction.currentE),
                    all_day: false,
                    is_all_day: false
                };
                dayTimedEvents = [...dayTimedEvents, previewAssignment];
            }
        }
    }

    const layouts = getTimedLayout(dayTimedEvents, interaction);
    const isDraggingInThisRow = interaction?.assignment && (toDateKey(interaction.currentDay || new Date(interaction.origS)) === key);

    // Styling constants
    const VERT_PADDING = 14;
    const MIN_CHIP_HEIGHT = 22;
    const LANE_GAP = 6;

    return (
        <div
            data-day={key}
            className="timeline-day-row"
            style={{ 
                height: dayActualHeight, 
                zIndex: isDraggingInThisRow ? 100 : (40 - dayIdx), 
                position: 'relative',
                overflow: 'visible',
                background: 'transparent'
            }}
        >
            {/* Day Label (Left Column) */}
            <div className={`timeline-day-label ${isToday ? 'today' : ''} ${isWeekend ? 'weekend' : ''}`} style={{ width: 80, minWidth: 80, height: dayActualHeight, borderRight: 'none', borderLeft: 'none', boxSizing: 'border-box' }}>
                <div className="day-name">{DAY_NAMES[day.getDay()]}</div>
                <div className="day-date">{String(day.getDate()).padStart(2, '0')}.{String(day.getMonth() + 1).padStart(2, '0')}</div>
            </div>

            {/* All-Day Drop Area (Center Column) */}
            <div 
                className="timeline-allday-col" 
                onDrop={(e) => !isReadOnly && handleDropAllDay(e, day)} 
                onDragOver={!isReadOnly ? handleDragOver : undefined} 
                onDragLeave={!isReadOnly ? handleDragLeave : undefined} 
                style={{ 
                    width: actualAlldayWidth, 
                    minWidth: actualAlldayWidth, 
                    maxWidth: actualAlldayWidth, 
                    height: dayActualHeight,
                    flex: 'none', 
                    padding: 0, 
                    overflow: 'visible', 
                    background: '#f8fafc', 
                    borderLeft: '2px solid #cbd5e1',
                    borderRight: 'none',
                    position: 'sticky', 
                    left: 80, 
                    zIndex: 109,
                    cursor: isReadOnly ? 'default' : 'pointer',
                    boxSizing: 'border-box'
                }}
                onClick={() => {
                    if (isReadOnly || interactionEnded || interaction) return;
                    const allDayDate = new Date(day);
                    allDayDate.setHours(0, 0, 0, 0);
                    onDayClick && onDayClick(allDayDate);
                }}
                onContextMenu={(e) => {
                    if (isReadOnly) return;
                    const allDayDate = new Date(day);
                    allDayDate.setHours(0, 0, 0, 0);
                    handleContextMenu(e, null, allDayDate);
                }}
            />

            {/* Time Grid (Right Column) */}
            <div
                className={`timeline-time-col ${isToday ? 'today' : ''} ${isWeekend ? 'weekend' : ''}`}
                style={{ minWidth: TIMELINE_WIDTH, height: dayActualHeight, clipPath: 'inset(-5000px 0)', borderLeft: '2px solid #cbd5e1', borderRight: 'none', boxSizing: 'border-box' }}
                onClick={(e) => {
                    if (isReadOnly || interactionEnded || interaction) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const offsetX = e.clientX - rect.left - GRID_BUFFER;
                    const hoursFromLeft = offsetX / dynamicHourWidth;
                    const time = new Date(day);
                    time.setHours(hourStart + Math.floor(hoursFromLeft), (hoursFromLeft % 1) * 60);
                    onDayClick && onDayClick(snapTo15Min(time));
                }}
                onDrop={(e) => !isReadOnly && handleDrop(e, day)}
                onDragOver={!isReadOnly ? handleDragOver : undefined}
                onDragLeave={!isReadOnly ? handleDragLeave : undefined}
                onContextMenu={(e) => {
                    if (isReadOnly) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const offsetX = e.clientX - rect.left - GRID_BUFFER;
                    const hoursFromLeft = offsetX / dynamicHourWidth;
                    const time = new Date(day);
                    time.setHours(hourStart + Math.floor(hoursFromLeft), (hoursFromLeft % 1) * 60);
                    handleContextMenu(e, null, day, snapTo15Min(time));
                }}
            >
                {/* Background Grid Lines */}
                <div className="time-grid-inner" style={{ 
                    minWidth: TIMELINE_WIDTH, 
                    position: 'absolute', 
                    top: 0, left: 0, bottom: 0, right: 0,
                    zIndex: 50,
                    pointerEvents: 'none'
                }}>
                    {Array.from({ length: hourEnd - hourStart + 1 }, (_, i) => (
                        <div key={i} className="timeline-hour-line-v" style={{ 
                            left: GRID_BUFFER + (i * dynamicHourWidth),
                            borderColor: 'rgba(203, 213, 225, 0.6)',
                            backgroundColor: 'rgba(203, 213, 225, 0.6)'
                        }} />
                    ))}
                </div>
                
                {/* Now Line */}
                {isToday && nowHour >= hourStart && nowHour <= hourEnd && (
                    <div 
                        className="timeline-now-line"
                        style={{
                            position: 'absolute',
                            top: 0, bottom: 0, left: nowLeft, width: '2px',
                            backgroundColor: '#ef4444',
                            zIndex: 450,
                            pointerEvents: 'none',
                            boxShadow: '0 0 8px rgba(239, 68, 68, 0.4)'
                        }}
                    >
                        <div style={{ position: 'absolute', top: 0, left: -3, width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ef4444' }} />
                    </div>
                )}

                {/* Timed Assignment Chips */}
                {layouts.map(event => {
                    const isInteracting = String(interaction?.assignment?.id) === String(event.id) && !event.isGhost && interaction.moved;
                    const isHidden = isInteracting && interaction.moved && !interaction.type.startsWith('resize');
                    let eStart = isInteracting && interaction.currentS ? interaction.currentS : new Date(event.start_datetime);
                    let eEnd = isInteracting && interaction.currentE ? interaction.currentE : new Date(event.end_datetime);
                    
                    if (isInteracting && interaction.type === 'move-allday' && !interaction.isOverAllDay && interaction.currentS) {
                        eStart = interaction.currentS;
                        eEnd = new Date(eStart.getTime() + 60 * 60 * 1000);
                    }

                    const currentDayStart = new Date(day); currentDayStart.setHours(0,0,0,0);
                    const currentDayEnd = new Date(day); currentDayEnd.setHours(23,59,59,999);
                    
                    if (eStart > currentDayEnd || eEnd < currentDayStart) return null;
                    
                    const effS = new Date(Math.max(eStart, currentDayStart));
                    const effE = new Date(Math.min(eEnd, currentDayEnd));

                    const sMin = (effS.getHours() * 60) + effS.getMinutes();
                    const eMin = (effE.getHours() * 60) + effE.getMinutes();
                    const viewSMin = hourStart * 60;
                    const viewEMin = hourEnd * 60;

                    if (eMin <= viewSMin || sMin >= viewEMin) return null;

                    const absSLeft = GRID_BUFFER + ((sMin - viewSMin) / 60) * dynamicHourWidth;
                    const absERight = GRID_BUFFER + ((eMin - viewSMin) / 60) * dynamicHourWidth;

                    const left = Math.max(0, absSLeft);
                    const right = Math.min(TIMELINE_WIDTH, absERight);
                    const width = Math.max(10, right - left);
                    
                    const availableHeight = dayActualHeight - VERT_PADDING;
                    const laneGap = event.totalCols > 1 ? LANE_GAP : 0;
                    const rowHeight = Math.max(MIN_CHIP_HEIGHT, (availableHeight - (laneGap * (event.totalCols - 1))) / Math.max(1, event.totalCols));
                    const baseTop = 7 + (event.col * (rowHeight + laneGap));
                    const topOffset = isInteracting ? baseTop + interaction.visualTopOffset : baseTop;

                    return (
                        <AssignmentChip
                            key={event.id}
                            assignment={event}
                            mode="timeline-timed"
                            hasConflict={assignmentConflictsMap[event.id]?.hasConflict}
                            isConflictDetectionEnabled={isConflictDetectionEnabled}
                            interaction={interaction}
                            scrollX={scrollX}
                            style={{
                                left, width, top: topOffset, height: rowHeight,
                                zIndex: isInteracting ? 150 : 60,
                                opacity: isHidden ? 0 : 1,
                                pointerEvents: isHidden ? 'none' : 'auto'
                            }}
                            onMouseEnter={(e) => onAssignmentHover?.(event, e)}
                            onMouseLeave={onAssignmentHoverEnd}
                            onMouseDown={(e, resizeType) => {
                                if (isReadOnly) return;
                                if (resizeType) {
                                    handleInteractionStart(e, resizeType, event, event.col);
                                } else {
                                    onAssignmentHoverEnd?.();
                                    onConflictHoverEnd?.();
                                    handleInteractionStart(e, 'move', event, event.col);
                                }
                            }}
                            onClick={(e) => {
                                if (interactionEnded) return;
                                e.stopPropagation();
                                e.preventDefault();
                                setTimeout(() => onAssignmentClick(event), 0);
                            }}
                            onContextMenu={(e) => { e.stopPropagation(); if (!isReadOnly) handleContextMenu(e, event); }}
                            onDrop={handleDrop}
                            onDragStart={(e) => {
                                if (isReadOnly) { e.preventDefault(); return; }
                                const img = new Image();
                                img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
                                e.dataTransfer.setDragImage(img, 0, 0);
                                const data = { type: 'assignment', assignment: event };
                                e.dataTransfer.setData('application/json', JSON.stringify(data));
                                e.dataTransfer.effectAllowed = 'move';
                            }}
                            onDragEnd={() => setInteraction(null)}
                        />
                    );
                })}
            </div>
        </div>
    );
}
